import { ProfileBody } from '@/components/dashboard/DashboardProfilePanel';
import { ApiError, api } from '@/lib/api';
import { auditDataQuality, profileColumns } from '@innermate/shared';
import { useQuery } from '@tanstack/react-query';
import { Loader2, X } from 'lucide-react';
import { useMemo } from 'react';

interface Props {
  artifactId: string;
  /** artifact 본문. inline JS 에 박힌 `const QUERY_ID = "<uuid>"` 에서 queryId 를 추출. */
  content: string;
  onClose: () => void;
}

/** dashboard 본문의 inline JS 에서 saved_query UUID 추출. render.ts 의 SPEC 주입 패턴과 정합. */
function extractQueryId(content: string): string | null {
  const m = content.match(/const QUERY_ID\s*=\s*["']([0-9a-f-]{36})["']/i);
  return m ? (m[1] ?? null) : null;
}

/**
 * chat artifact 의 dashboard 에 대한 분석 패널.
 * 신규 서버 endpoint 없이 기존 artifact RPC (runSavedQuery) 의 응답 rows 를 client-side 에서
 * profileColumns + auditDataQuality (shared/analytics/profile.ts) 로 계산.
 */
export function ArtifactProfilePanel({ artifactId, content, onClose }: Props) {
  const queryId = useMemo(() => extractQueryId(content), [content]);

  const profileQuery = useQuery({
    queryKey: ['artifact', artifactId, 'profile', queryId],
    enabled: !!queryId,
    queryFn: async () => {
      if (!queryId) throw new Error('queryId 를 찾을 수 없습니다.');
      const res = await api.artifactRpc(artifactId, 'runSavedQuery', { queryId, maxRows: 10000 });
      if (!res.ok) throw new Error(res.error);
      // RPC 응답이 row-as-object 배열로 정규화돼 있음 (server 가 normalize)
      const data = res.data as {
        columns: string[];
        rows: Array<Record<string, unknown>>;
        rowCount: number;
        truncated: boolean;
      };
      const columns = profileColumns(data.rows);
      const audit = auditDataQuality(data.rows, columns);
      return { columns, audit, rowCount: data.rowCount, truncated: data.truncated };
    },
    staleTime: 5 * 60_000,
  });

  return (
    <aside className="fixed right-0 top-0 bottom-0 z-40 w-[420px] max-w-[90vw] flex flex-col border-l border-border bg-surface shadow-2xl">
      <header className="flex h-12 shrink-0 items-center gap-2 border-b border-border px-3">
        <h3 className="flex-1 text-sm font-semibold">분석</h3>
        <button
          type="button"
          onClick={onClose}
          aria-label="분석 패널 닫기"
          className="grid size-7 place-items-center rounded-md text-foreground-muted hover:bg-surface-muted hover:text-foreground"
        >
          <X className="size-4" />
        </button>
      </header>
      <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-4">
        <section className="rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-[11px] text-amber-700">
          설계 방향·비주얼 디렉션·브랜드 색 변경은 <b>저장된 대시보드</b>에서만 가능합니다. 우상단의
          저장(BookmarkPlus) 으로 먼저 저장하세요.
        </section>

        {!queryId ? (
          <div className="rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-3 text-xs text-amber-700">
            이 artifact 에서 saved_query UUID 를 찾지 못했습니다. dashboard 가 아닐 수 있어요.
          </div>
        ) : profileQuery.isLoading ? (
          <div className="flex items-center justify-center py-8 text-sm text-foreground-muted">
            <Loader2 className="mr-2 size-4 animate-spin" /> 분석 중…
          </div>
        ) : profileQuery.isError ? (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-3 text-xs text-destructive">
            {profileQuery.error instanceof ApiError
              ? profileQuery.error.message
              : profileQuery.error instanceof Error
                ? profileQuery.error.message
                : '분석을 불러오지 못했습니다.'}
          </div>
        ) : profileQuery.data ? (
          <ProfileBody
            audit={profileQuery.data.audit}
            columns={profileQuery.data.columns}
            rowCount={profileQuery.data.rowCount}
            truncated={profileQuery.data.truncated}
          />
        ) : null}
      </div>
    </aside>
  );
}
